const fleetDetails = {
  codeName: 'fleetDetails',
  displayName: 'Fleet Details',
  imagePath:
    '/getmedia/406f500a-1f7b-44a9-922b-c600de31e19b/icon-fleet-details.svg',
};
const tires = {
  codeName: 'tires',
  displayName: 'Trailer Tires',
  imagePath: '/getmedia/7575d14b-d184-4e1e-9e1f-26aa74aff648/icon-tires.svg',
};
const fuel = {
  codeName: 'fuel',
  displayName: 'Fuel',
  imagePath: '/getmedia/957bef45-7a3b-455a-83a6-0ca477aa0ab9/icon-fuel.svg',
};
const maintenance = {
  codeName: 'maintenance',
  displayName: 'Maintenance',
  imagePath:
    '/getmedia/3ce0d23f-0136-4671-961f-1c89718b8809/icon-maintenance.svg',
};
const roadCalls = {
  codeName: 'roadCalls',
  displayName: 'Road Calls',
  imagePath: '/getmedia/f67606c6-6730-41b3-9cca-37ae2bdeb30d/icon-truck.svg',
};
const review = {
  codeName: 'review',
  displayName: 'Review',
  imagePath: '/getmedia/af82ae6b-3404-4ee3-a0a0-3b97941f5da2/icon-review.svg',
  nextStepLabel: 'Calculate Savings',
};
const summary = {
  codeName: 'summary',
  displayName: 'TIREMAAX<sup>®</sup> PRO SAVINGS SUMMARY',
  shortName: 'Summary',
  imagePath: '/getmedia/c126cbce-893a-456e-ba5d-0511f27aa5e7/icon-summary.svg',
};

/** @returns {FleetDetails} */
export const defaultFleetDetails = () => ({
  tractor: {
    total: 100,
    lifecycle: 5,
    annualDistance: 90000,
  },
  trailer: {
    total: 300,
    lifecycle: 7,
    annualDistance: 30000,
  },
});
/** @returns {Tires} */
export const defaultTires = (/** @type {TireStyle} */ tireStyle) => {
  const tires = {
    tireStyle,
    costOfTire: 400.0,
    tireLife: 117000,
    costCustom: false,
  };

  setCostOfTire(tires, tireStyle);

  return tires;
};
/** @returns {Fuel} */
export const defaultFuel = () => ({
  costOfFuel: 3.16,
  tractorMPG: 7.0,
});
/** @returns {Maintenance} */
export const defaultMaintenance = () => ({
  numberOfChecks: 4,
  laborTime: 13,
  laborRate: 42.11,
});
/** @returns {RoadCalls} */
export const defaultRoadCalls = (/** @type {TireStyle} */ tireStyle) => {
  const roadCalls = {
    tireRelatedBreakdownsPerYear: 0.9,
    costOfRoadCalls: 500.0,
    tireReplacementCost: 0,
    downtimePerHourCost: 42.11,
    downtimeRoadCallsHours: 2.55,
  };

  setTireReplacementCost(roadCalls, defaultTires(tireStyle));

  return roadCalls;
};

/** @returns {TireStyle} */
export function getDefaultTireStyle() {
  return 'Dual';
}

/**
 *
 * @param {Tires} tires
 * @param {TireStyle} tireStyle
 */
export function setCostOfTire(tires, tireStyle) {
  tires.costOfTire = tireStyle === 'Super Single' ? 850 : 400;
}

/**
 * @param {RoadCalls} roadCalls
 * @param {Tires} tires
 */
export function setTireReplacementCost(roadCalls, tires) {
  roadCalls.tireReplacementCost = tires.costOfTire;
}

/**
 * @type {ROICalculatorState}
 */
export const state = {
  landingPageImagePath:
    '/getmedia/cecf9f21-7a00-425b-809b-dcbd09231fc1/splash-image.png',

  currentStep: undefined,

  steps: [fleetDetails, tires, fuel, maintenance, roadCalls, review, summary],

  stepsSet: {
    fleetDetails,
    fuel,
    maintenance,
    review,
    roadCalls,
    summary,
    tires,
  },

  calcConstants: {
    TIREMAAXRoadCallsRate: 0.2,
    TIREMAAXTireLifetimeImprovementRate: 0.15384615,
    fuelEfficiency: 0.015,
    costOfTIREMAAXDollars: 900,
  },

  formValues: {
    fleetDetails: defaultFleetDetails(),
    tires: defaultTires(getDefaultTireStyle()),
    fuel: defaultFuel(),
    maintenance: defaultMaintenance(),
    roadCalls: defaultRoadCalls(getDefaultTireStyle()),
  },

  calculation: {
    fleetTotals: {
      annualFleetSavingsDollars: 0,
      annualFuelSavingsFleetDollars: 0,
      maintenanceSavingsFleetDollars: 0,
      roadCallSavingsFleetDollars: 0,
      tireSavingsForFleetDollars: 0,
    },

    payback: {
      paybackOnInvestmentFleetMonths: 0,
      paybackOnInvestmentTrailerMonths: 0,
    },

    perMile: {
      fuelSavingsPerMileCents: 0,
      maintenanceSavingsPerMileCents: 0,
      roadCallSavingsPerMileCents: 0,
      tireSavingsPerMileCents: 0,
      totalPerMileCents: 0,
    },

    trailerTotals: {
      annualFuelSavingsPerTrailerDollars: 0,
      annualTrailerSavingsDollars: 0,
      maintenanceSavingsPerTrailerDollars: 0,
      roadCallSavingsPerTrailerDollars: 0,
      tireSavingsPerTrailerDollars: 0,
    },
  },
};

/**
 * @typedef Step
 * @property {string} codeName
 * @property {string} displayName
 * @property {string=} shortName
 * @property {string} imagePath
 * @property {string=} nextStepLabel
 */

/**
 * @typedef StepsSet
 * @property {Step} fleetDetails
 * @property {Step} tires
 * @property {Step} fuel
 * @property {Step} maintenance
 * @property {Step} roadCalls
 * @property {Step} review
 * @property {Step} summary
 */

/**
 * @typedef FleetDetails
 * @property {{ total: number, lifecycle: number, annualDistance: number }} tractor
 * @property {{ total: number, lifecycle: number, annualDistance: number }} trailer
 */

/**
 * @typedef {'Dual' | 'Super Single'} TireStyle
 */

/**
 * @typedef Tires
 * @property {TireStyle} tireStyle
 * @property {number} costOfTire
 * @property {number} tireLife
 * @property {boolean} costCustom
 */

/**
 * @typedef Fuel
 * @property {number} costOfFuel
 * @property {number} tractorMPG
 */

/**
 * @typedef Maintenance
 * @property {number} numberOfChecks
 * @property {number} laborTime
 * @property {number} laborRate
 */

/**
 * @typedef RoadCalls
 * @property {number} tireRelatedBreakdownsPerYear
 * @property {number} costOfRoadCalls
 * @property {number} tireReplacementCost
 * @property {number} downtimeRoadCallsHours
 * @property {number} downtimePerHourCost
 */

/**
 * @typedef FormData
 * @property {FleetDetails} fleetDetails
 * @property {Tires} tires
 * @property {Fuel} fuel
 * @property {Maintenance} maintenance
 * @property {RoadCalls} roadCalls
 */

/**
 * @typedef CalculationConstants
 * @property {0.2} TIREMAAXRoadCallsRate
 * @property {0.15384615} TIREMAAXTireLifetimeImprovementRate
 * @property {0.015} fuelEfficiency
 * @property {900} costOfTIREMAAXDollars
 */

/**
 * @typedef Calculation
 * @property {{ paybackOnInvestmentFleetMonths: number, paybackOnInvestmentTrailerMonths: number }} payback
 * @property {{
 *  annualFleetSavingsDollars: number,
 *  annualFuelSavingsFleetDollars: number,
 *  roadCallSavingsFleetDollars: number,
 *  tireSavingsForFleetDollars: number,
 *  maintenanceSavingsFleetDollars: number }} fleetTotals
 * @property {{
 *  annualTrailerSavingsDollars: number,
 *  annualFuelSavingsPerTrailerDollars: number,
 *  roadCallSavingsPerTrailerDollars: number,
 *  tireSavingsPerTrailerDollars: number,
 *  maintenanceSavingsPerTrailerDollars: number }} trailerTotals
 * @property {{
 *  fuelSavingsPerMileCents: number,
 *  roadCallSavingsPerMileCents: number,
 *  tireSavingsPerMileCents: number,
 *  maintenanceSavingsPerMileCents: number,
 *  totalPerMileCents: number }} perMile
 */

/**
 * @typedef ROICalculatorState
 * @property {Step=} currentStep
 * @property {Step[]} steps
 * @property {StepsSet} stepsSet
 * @property {CalculationConstants} calcConstants
 * @property {Calculation} calculation
 * @property {FormData} formValues
 * @property {string} landingPageImagePath
 */
